import React, { useState } from 'react';
import { ParkingLotTable } from '../../components/ParkingLotTable';
import { ParkingLotModal } from '../../components/ParkingLotModal';
import ParkingLotsEditorFilters from '../../components/ParkingLotsEditorFilters';
import ParkingLotsMapView from './ParkingLotsMapView';
import ParkingLotFeedbackTab from './ParkingLotFeedbackTab';
import { useCities, usePublicParkingLotsList } from '../../lib/api';
import {
    DEFAULT_PARKING_LOTS_EDITOR_FILTERS,
    type ParkingLotsEditorFiltersState,
} from '../../lib/parkingLotsEditorFilters';
import { ParkingLot } from '../../lib/types';
import LoadingState from '../../components/common/LoadingState';
import ErrorState from '../../components/common/ErrorState';
import Pagination from '../../components/common/Pagination';
import { List, MapPin } from 'lucide-react';

const DEFAULT_CITY = 'aalborg';

type ViewMode = 'list' | 'map';
type MainTab = 'editor' | 'feedback';

const ParkingLotsPage: React.FC = () => {
    const [mainTab, setMainTab] = useState<MainTab>('editor');
    const [viewMode, setViewMode] = useState<ViewMode>('map');
    const [selectedCity, setSelectedCity] = useState(DEFAULT_CITY);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('address');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [selectedParkingLot, setSelectedParkingLot] = useState<ParkingLot | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editorFilters, setEditorFilters] = useState<ParkingLotsEditorFiltersState>(
        DEFAULT_PARKING_LOTS_EDITOR_FILTERS
    );

    const { cities } = useCities();
    const { parkingLots, pagination, isLoading: listLoading, error, mutate } = usePublicParkingLotsList(
        selectedCity,
        currentPage,
        pageSize,
        searchTerm,
        sortBy,
        sortOrder,
        viewMode === 'list',
        editorFilters
    );
    const isLoading = viewMode === 'list' ? listLoading : false;

    const handleSearch = (term: string) => {
        setSearchTerm(term);
        setCurrentPage(1); // Reset to first page when searching
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setCurrentPage(1); // Reset to first page when changing page size
    };

    const handleCityChange = (city: string) => {
        setSelectedCity(city);
        setCurrentPage(1);
    };

    const handleViewModeChange = (mode: ViewMode) => {
        if (mode === 'map' && selectedCity === 'all') {
            setSelectedCity(DEFAULT_CITY);
        }
        setViewMode(mode);
    };

    const handleEditorFiltersChange = (next: ParkingLotsEditorFiltersState) => {
        setEditorFilters(next);
        setCurrentPage(1);
    };

    const handleSort = (newSortBy: string, newSortOrder: 'asc' | 'desc') => {
        setSortBy(newSortBy);
        setSortOrder(newSortOrder);
        setCurrentPage(1); // Reset to first page when sorting
    };

    const handleViewDetails = (parkingLot: ParkingLot) => {
        setSelectedParkingLot(parkingLot);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedParkingLot(null);
    };

    const handleUpdateParkingLot = (_updatedLot: ParkingLot) => {
        mutate();
    };

    const mainTabSwitcher = (
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
                type="button"
                onClick={() => setMainTab('editor')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    mainTab === 'editor'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                }`}
            >
                Rediger
            </button>
            <button
                type="button"
                onClick={() => setMainTab('feedback')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    mainTab === 'feedback'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                }`}
            >
                Offentlig feedback
            </button>
        </div>
    );

    if (mainTab === 'feedback') {
        return (
            <div className="h-full flex flex-col min-h-0">
                <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-wrap gap-2">
                    <h1 className="text-xl md:text-2xl font-bold text-gray-900">Parking Lots</h1>
                    {mainTabSwitcher}
                </div>
                <div className="flex-1 min-h-0 overflow-hidden">
                    <ParkingLotFeedbackTab />
                </div>
            </div>
        );
    }

    if (viewMode === 'list' && isLoading) {
        return (
            <div className="space-y-4 p-4">
                <div className="flex flex-col gap-3">
                    <div className="flex justify-end">{mainTabSwitcher}</div>
                    <ParkingLotsEditorFilters
                        value={editorFilters}
                        onChange={handleEditorFiltersChange}
                        showMapContextControls={false}
                    />
                </div>
                <LoadingState message="Loading parking lots..." />
            </div>
        );
    }
    if (viewMode === 'list' && error) {
        return (
            <div className="space-y-4 p-4">
                <div className="flex flex-col gap-3">
                    <div className="flex justify-end">{mainTabSwitcher}</div>
                    <ParkingLotsEditorFilters
                        value={editorFilters}
                        onChange={handleEditorFiltersChange}
                        showMapContextControls={false}
                    />
                </div>
                <ErrorState error={error.message} onRetry={() => mutate()} />
            </div>
        );
    }

    // Render map view
    if (viewMode === 'map') {
        return (
            <div className="h-full flex flex-col">
                {/* Header with city selector and view toggle */}
                <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-wrap">
                        <h1 className="text-xl md:text-2xl font-bold text-gray-900">Parking Lots</h1>
                        <label className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">City:</span>
                            <select
                                value={selectedCity}
                                onChange={(e) => handleCityChange(e.target.value)}
                                className="border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {cities.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </label>
                    </div>

                <div className="flex items-center gap-2 flex-wrap">
                    {mainTabSwitcher}
                    {/* View Toggle */}
                    <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                        <button
                            onClick={() => handleViewModeChange('list')}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${
                                (viewMode as ViewMode) === 'list'
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            <List className="h-4 w-4" />
                            Liste
                        </button>
                        <button
                            onClick={() => handleViewModeChange('map')}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${
                                (viewMode as ViewMode) === 'map'
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            <MapPin className="h-4 w-4" />
                            Kort
                        </button>
                    </div>
                </div>
            </div>

            <div className="border-b border-gray-200 bg-white px-4 py-2">
                <ParkingLotsEditorFilters
                    value={editorFilters}
                    onChange={handleEditorFiltersChange}
                    showMapContextControls={true}
                />
            </div>

            {/* Map View */}
            <div className="flex-1 overflow-hidden">
                <ParkingLotsMapView
                    selectedCity={selectedCity}
                    cities={cities}
                    editorFilters={editorFilters}
                />
            </div>
        </div>
    );
}

// Render list view
return (
    <div className="space-y-4 md:space-y-6">
        {/* Header Section */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
                <h1 className="text-xl md:text-2xl font-bold text-gray-900">Parking Lots</h1>
                <p className="text-sm md:text-base text-gray-600 mt-1">
                    Manage and view parking lot information
                    {pagination && (
                        <span className="ml-2 text-blue-600 font-medium">
                            • {pagination.total.toLocaleString()} total parking lots
                        </span>
                    )}
                </p>
            </div>

            {/* View Toggle, City selector and Page Size Control */}
            <div className="flex items-center gap-2 md:gap-4 flex-wrap">
                <label className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">City:</span>
                    <select
                        value={selectedCity}
                        onChange={(e) => handleCityChange(e.target.value)}
                        className="border border-gray-300 rounded-md px-2 py-1 md:px-3 md:py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="all">All</option>
                        {cities.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </label>
                {mainTabSwitcher}
                {/* View Toggle */}
                <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                    <button
                        onClick={() => handleViewModeChange('list')}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${
                            (viewMode as ViewMode) === 'list'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        <List className="h-4 w-4" />
                        Liste
                    </button>
                    <button
                        onClick={() => handleViewModeChange('map')}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${
                            (viewMode as ViewMode) === 'map'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        <MapPin className="h-4 w-4" />
                        Kort
                    </button>
                </div>

                {/* Page Size Control */}
                <div className="flex items-center gap-2">
                    <label htmlFor="pageSize" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                        Show:
                    </label>
                    <select
                        id="pageSize"
                        value={pageSize}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        className="border border-gray-300 rounded-md px-2 py-1 md:px-3 md:py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                    </select>
                    <span className="text-sm text-gray-600 hidden sm:inline">per page</span>
                </div>
            </div>
        </div>

            <ParkingLotsEditorFilters
                value={editorFilters}
                onChange={handleEditorFiltersChange}
                showMapContextControls={false}
            />

            {/* Pagination Info */}
            {pagination && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4">
                    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                        <div className="text-sm text-blue-800">
                            <span className="font-medium">
                                Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                                {pagination.total.toLocaleString()} parking lots
                            </span>
                        </div>
                        <div className="text-sm text-blue-600">
                            Page {pagination.page} of {pagination.pages}
                        </div>
                    </div>
                </div>
            )}

            {/* Table Section */}
            <div className="overflow-hidden">
                <ParkingLotTable
                    parkingLots={parkingLots || []}
                    isLoading={isLoading}
                    error={error}
                    onSearch={handleSearch}
                    searchTerm={searchTerm}
                    onViewDetails={handleViewDetails}
                    onSort={handleSort}
                    currentSortBy={sortBy}
                    currentSortOrder={sortOrder}
                />
            </div>

            {/* Pagination Controls */}
            {pagination && pagination.pages > 1 && (
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <Pagination
                        pagination={{
                            page: pagination.page,
                            pages: pagination.pages,
                            total: pagination.total,
                            hasNext: pagination.page < pagination.pages,
                            hasPrev: pagination.page > 1
                        }}
                        onPageChange={handlePageChange}
                    />

                    <div className="text-sm text-gray-600 text-center md:text-right">
                        {pagination.total.toLocaleString()} total parking lots
                    </div>
                </div>
            )}

            {/* Modal */}
            <ParkingLotModal
                parkingLot={selectedParkingLot}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onUpdate={handleUpdateParkingLot}
            />
        </div>
    );
};

export default ParkingLotsPage;
